import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Featured Product Stateful Component
 * Um componente que gerencia uma lista de produtos em destaque com estado stateful
 * Intercepta eventos de mudança para atualizar conteúdo sem movimento horizontal
 */
class FeaturedProductStateful extends Component {
  constructor() {
    super();
    
    // Estado interno
    this.currentIndex = 0;
    this.productsData = /** @type {Array<any>} */ ([]);
    this.isInitialized = false;
    this.benefitsCarouselIndex = 0;
    this.intersectionObserver = null;
    
    // Controle de performance
    this.imagePreloadQueue = new Set();
    this.abortController = new AbortController();
    
    this.init();
  }

  /**
   * Inicializa o componente
   */
  async init() {
    try {
      this.loadProductsData();
      this.setupElements();
      this.setupEventListeners();
      this.setupIntersectionObserver();
      this.initializeBenefitsCarousel();
      this.preloadNextImages();
      
      this.isInitialized = true;
      this.dispatchEvent(new CustomEvent('featured-product:initialized'));
    } catch (error) {
      console.error('Erro ao inicializar FeaturedProductStateful:', error);
    }
  }

  /**
   * Carrega os dados dos produtos do JSON inline
   */
  loadProductsData() {
    const dataScript = this.querySelector('[data-products-data]');
    if (dataScript && dataScript.textContent) {
      try {
        this.productsData = JSON.parse(dataScript.textContent);
      } catch (error) {
        console.error('Erro ao parsear dados dos produtos:', error);
        this.productsData = [];
      }
    }
  }

  /**
   * Configura elementos e referências
   */
  setupElements() {
    // Desktop elements
    this.desktopContainer = this.querySelector('.featured-product-desktop');
    this.productTitle = this.querySelector('[data-product-title]');
    this.productDescription = this.querySelector('[data-product-description]');
    this.inlineBenefits = this.querySelector('[data-inline-benefits]');
    this.productCta = this.querySelector('[data-product-cta]');
    this.mainImage = this.querySelector('[data-main-image]');
    this.thumbnails = this.querySelector('[data-thumbnails]');
    this.productReview = this.querySelector('[data-product-review]');
    
    // Mobile elements
    this.mobileContainer = this.querySelector('.featured-product-mobile');
    this.productTitleMobile = this.querySelector('[data-product-title-mobile]');
    this.productDescriptionMobile = this.querySelector('[data-product-description-mobile]');
    this.mainImageMobile = this.querySelector('[data-main-image-mobile]');
    this.thumbnailsMobile = this.querySelector('[data-thumbnails-mobile]');
    this.productReviewMobile = this.querySelector('[data-product-review-mobile]');
    this.productCtaMobile = this.querySelector('[data-product-cta-mobile]');
    
    // Benefits grid
    this.benefitsGrid = this.querySelector('[data-benefits-grid]');
    this.benefitsContainer = this.querySelector('.benefits-container');
    this.benefitsPrev = this.querySelector('.benefits-prev');
    this.benefitsNext = this.querySelector('.benefits-next');
    this.benefitsIndicators = this.querySelector('.benefits-indicators');
    
    // Set initial state
    this.dataset.currentIndex = '0';
  }

  /**
   * Configura event listeners
   */
  setupEventListeners() {
    const { signal } = this.abortController;
    
    // Thumbnails desktop
    if (this.thumbnails) {
      this.thumbnails.querySelectorAll('[data-product-index]').forEach(thumbnail => {
        thumbnail.addEventListener('click', this.handleThumbnailClick.bind(this), { signal });
      });
    }
    
    // Thumbnails mobile
    if (this.thumbnailsMobile) {
      this.thumbnailsMobile.querySelectorAll('[data-product-index]').forEach(thumbnail => {
        thumbnail.addEventListener('click', this.handleThumbnailClick.bind(this), { signal });
      });
    }
    
    // Benefits carousel mobile
    if (this.benefitsPrev) {
      this.benefitsPrev.addEventListener('click', this.handleBenefitsPrev.bind(this), { signal });
    }
    
    if (this.benefitsNext) {
      this.benefitsNext.addEventListener('click', this.handleBenefitsNext.bind(this), { signal });
    }
    
    // Keyboard navigation
    this.addEventListener('keydown', this.handleKeydown.bind(this), { signal });
    
    // Window resize for responsive adjustments
    window.addEventListener('resize', /** @type {EventListener} */ (debounce(this.handleResize.bind(this), 250)), { signal });
    
    // Custom events
    this.addEventListener('product:changed', this.handleProductChanged.bind(this), { signal });
    
    // Eventos de clique para elementos clicáveis
    this.querySelectorAll('[data-clickable]').forEach(element => {
      element.addEventListener('click', this.handleClickableElement.bind(this), { signal });
      if (element instanceof HTMLElement) {
        element.style.cursor = 'pointer';
      }
    });
  }

  /**
   * Configura Intersection Observer para preload de imagens
   */
  setupIntersectionObserver() {
    if (!('IntersectionObserver' in window)) return;
    
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.preloadNextImages();
          }
        });
      },
      { rootMargin: '100px' }
    );
    
    this.intersectionObserver.observe(this);
  }

  /**
   * Manipula clique nos thumbnails
   * @param {Event} event 
   */
  handleThumbnailClick(event) {
    if (!event.target) return;
    const thumbnailBtn = /** @type {Element} */ (event.target).closest('[data-product-index]');
    if (!thumbnailBtn) return;
    
    event.preventDefault();
    
    const newIndex = parseInt(/** @type {HTMLElement} */ (thumbnailBtn).dataset.productIndex || '0', 10);
    if (newIndex !== this.currentIndex) {
      this.selectProduct(newIndex);
    }
  }

  /**
   * Manipula navegação por teclado
   * @param {Event} event 
   */
  handleKeydown(event) {
    if (!this.isInitialized) return;
    
    const keyboardEvent = /** @type {KeyboardEvent} */ (event);
    switch (keyboardEvent.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previous();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      case 'Home':
        event.preventDefault();
        this.selectProduct(0);
        break;
      case 'End':
        event.preventDefault();
        this.selectProduct(this.productsData.length - 1);
        break;
    }
  }

  /**
   * Manipula redimensionamento da janela
   */
  handleResize() {
    // Recalcular layout se necessário
    if (window.innerWidth <= 768) {
      this.updateMobileBenefitsCarousel();
    }
  }

  /**
   * Manipula evento customizado de mudança de produto
   * @param {CustomEvent} event 
   */
  handleProductChanged(event) {
    // Evento para outros componentes reagirem à mudança
    // console.log('Produto mudou:', event.detail);
  }

  /**
   * Seleciona um produto pelo índice
   * @param {number} index 
   */
  async selectProduct(index) {
    if (index < 0 || index >= this.productsData.length || index === this.currentIndex) {
      return;
    }

    const previousIndex = this.currentIndex;
    this.currentIndex = index;
    
    // Atualizar data attribute
    this.dataset.currentIndex = index.toString();
    this.updateThumbnails(); // Thumbnails não fazem fade

    console.log('selectProduct before fadeOutElements', index);
    
    // Aplicar fade-out aos elementos (exceto thumbnails)
    await this.fadeOutElements();

    console.log('selectProduct after fadeOutElements', index);
    
    // Atualizar UI (com conteúdo transparente)
    this.updateProductInfo();
    this.updateMainImage();
    this.updateProductReview();
    this.updateBenefitsGrid();
    this.updateCTALinks();
    
    // Aplicar fade-in aos elementos
    await this.fadeInElements();
    
    // Preload próximas imagens
    this.preloadNextImages();
    
    // Disparar evento customizado
    this.dispatchEvent(new CustomEvent('product:changed', {
      detail: {
        previousIndex,
        currentIndex: this.currentIndex,
        product: this.productsData[index]
      },
      bubbles: true
    }));
    
    // Anunciar mudança para screen readers
    this.announceProductChange(index);
  }

  /**
   * Aplica fade-out aos elementos que devem fazer transição
   * Exclui os containers de imagem que têm animação própria
   * @returns {Promise<void>}
   */
  async fadeOutElements() {
    const elementsToFade = [
      this.productTitle,
      this.productDescription,
      this.inlineBenefits,
      this.productCta,
      // this.mainImage, // Removido: tem animação própria
      this.productReview,
      this.benefitsGrid,
      // Mobile elements
      this.productTitleMobile,
      this.productDescriptionMobile,
      // this.mainImageMobile, // Removido: tem animação própria
      this.productCtaMobile
    ];

    // Aplicar fade-out
    elementsToFade.forEach(element => {
      if (element) {
        /** @type {HTMLElement} */ (element).style.opacity = '0';
      }
    });

    // Aguardar 100ms (metade da animação de 200ms)
    return new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Aplica fade-in aos elementos que fizeram transição
   * Exclui os containers de imagem que têm animação própria
   * @returns {Promise<void>}
   */
  async fadeInElements() {
    const elementsToFade = [
      this.productTitle,
      this.productDescription,
      this.inlineBenefits,
      this.productCta,
      // this.mainImage, // Removido: tem animação própria
      this.productReview,
      this.benefitsGrid,
      // Mobile elements
      this.productTitleMobile,
      this.productDescriptionMobile,
      // this.mainImageMobile, // Removido: tem animação própria
      this.productCtaMobile
    ];

    // Aplicar fade-in
    elementsToFade.forEach(element => {
      if (element) {
        /** @type {HTMLElement} */ (element).style.opacity = '1';
      }
    });

    // Aguardar 100ms para completar a animação
    return new Promise(resolve => setTimeout(resolve, 100));
  }

  /**
   * Navega para o próximo produto
   */
  next() {
    const nextIndex = (this.currentIndex + 1) % this.productsData.length;
    this.selectProduct(nextIndex);
  }

  /**
   * Navega para o produto anterior
   */
  previous() {
    const prevIndex = this.currentIndex === 0 ? this.productsData.length - 1 : this.currentIndex - 1;
    this.selectProduct(prevIndex);
  }

  /**
   * Atualiza informações do produto (título, descrição)
   */
  updateProductInfo() {
    const product = this.productsData[this.currentIndex];
    if (!product) return;
    
    // Desktop
    if (this.productTitle) {
      this.productTitle.textContent = product.title;
    }
    
    if (this.productDescription) {
      this.productDescription.innerHTML = product.description ? `<p>${product.description}</p>` : '';
    }
    
    // Mobile
    if (this.productTitleMobile) {
      this.productTitleMobile.textContent = product.title;
    }
    
    if (this.productDescriptionMobile) {
      this.productDescriptionMobile.innerHTML = product.description ? `<p>${product.description}</p>` : '';
    }
    
    // Benefícios inline
    this.updateInlineBenefits(product.benefits);
  }

  /**
   * Atualiza benefícios inline
   * @param {Array<any>} benefits 
   */
  updateInlineBenefits(benefits) {
    if (!this.inlineBenefits || !benefits) return;
    
    const benefitsToShow = benefits.slice(0, 4);
    this.inlineBenefits.innerHTML = '';
    
         benefitsToShow.forEach(/** @param {any} benefit */ (benefit) => {
       if (benefit && benefit.imagemSmall) {
        const benefitItem = document.createElement('div');
        benefitItem.className = 'inline-benefit-item';
        
        const img = document.createElement('img');
        img.src = benefit.imagemSmall;
        img.alt = benefit.titulo || '';
        img.className = 'benefit-icon';
        img.setAttribute('role', 'img');
        img.setAttribute('aria-label', benefit.titulo || '');
        img.setAttribute('loading', 'lazy');
        img.setAttribute('width', '32');
        img.setAttribute('height', '32');
        
        benefitItem.appendChild(img);
        this.inlineBenefits?.appendChild(benefitItem);
      }
    });
  }

  /**
   * Atualiza imagem principal
   */
  updateMainImage() {
    const product = this.productsData[this.currentIndex];
    if (!product) return;
    
    // Desktop
    if (this.mainImage) {
      this.updateImageContainer(this.mainImage, product.mainImage, product.title, 'product-main-img');
    }
    
    // Mobile
    if (this.mainImageMobile) {
      this.updateImageContainer(this.mainImageMobile, product.mobileImage, product.title, 'product-main-img-mobile');
    }
  }

  /**
   * Atualiza container de imagem com animação de slide
   * @param {Element} container 
   * @param {string} imageSrc 
   * @param {string} alt 
   * @param {string} className 
   */
  updateImageContainer(container, imageSrc, alt, className) {
    if (!container) return;

    if (imageSrc) {
      this.performSlideAnimation(container, imageSrc, alt, className);
    } else {
      this.showPlaceholder(container);
    }
  }

  /**
   * Executa a animação de slide entre imagens
   * @param {Element} container 
   * @param {string} newImageSrc 
   * @param {string} alt 
   * @param {string} className 
   */
  async performSlideAnimation(container, newImageSrc, alt, className) {
    const currentImage = container.querySelector('.slide-image, img');
    
    // Criar nova imagem
    const newImage = document.createElement('img');
    newImage.src = newImageSrc;
    newImage.alt = alt || '';
    
    // Se não há imagem atual, apenas mostrar a nova sem animação
    if (!currentImage || !/** @type {HTMLImageElement} */ (currentImage).src) {
      newImage.className = `slide-image current ${className}`;
      container.innerHTML = '';
      container.appendChild(newImage);
      return;
    }

    // Log para debug
    console.log('🎬 Iniciando animação de slide');
    
    // Garantir que a imagem atual tem as classes corretas
    currentImage.className = `slide-image current ${className}`;
    
    // Preparar nova imagem começando fora da tela (direita)
    newImage.className = `slide-image slide-in-from-right ${className}`;
    container.appendChild(newImage);

    // Aguardar o carregamento da nova imagem
    await new Promise((resolve) => {
      if (newImage.complete) {
        resolve(undefined);
      } else {
        newImage.onload = () => resolve(undefined);
        newImage.onerror = () => resolve(undefined);
      }
    });

    // Aguardar um frame para garantir que os estilos foram aplicados
    await new Promise(resolve => requestAnimationFrame(resolve));

    console.log('🎬 Aplicando classes de animação');
    
    // Iniciar animações simultaneamente
    currentImage.classList.remove('current');
    currentImage.classList.add('slide-out-left');
    
    newImage.classList.remove('slide-in-from-right');
    newImage.classList.add('slide-in-center');

    // Aguardar o fim da animação (500ms + buffer)
    await new Promise(resolve => setTimeout(resolve, 550));

    console.log('🎬 Finalizando animação e limpando');

    // Limpar: remover imagem antiga e simplificar classes da nova
    if (currentImage && currentImage.parentNode) {
      currentImage.remove();
    }
    
    // Limpar classes de animação da nova imagem
    newImage.className = `slide-image current ${className}`;
  }

  /**
   * Mostra placeholder quando não há imagem
   * @param {Element} container 
   */
  showPlaceholder(container) {
    const currentImage = container.querySelector('.slide-image, img');
    
    if (currentImage) {
      // Animar saída da imagem atual
      currentImage.classList.remove('current');
      currentImage.classList.add('slide-out-left');
      
      setTimeout(() => {
        container.innerHTML = '<div class="placeholder-image"><svg width="100" height="100" viewBox="0 0 100 100" fill="none"><rect width="100" height="100" fill="#f5f5f5"/><path d="M30 30h40v40H30z" stroke="#ccc" stroke-width="2" fill="none"/></svg></div>';
      }, 500);
    } else {
      container.innerHTML = '<div class="placeholder-image"><svg width="100" height="100" viewBox="0 0 100 100" fill="none"><rect width="100" height="100" fill="#f5f5f5"/><path d="M30 30h40v40H30z" stroke="#ccc" stroke-width="2" fill="none"/></svg></div>';
    }
  }

  /**
   * Atualiza thumbnails ativos
   */
  updateThumbnails() {
    // Desktop thumbnails
    if (this.thumbnails) {
      const thumbButtons = this.thumbnails.querySelectorAll('[data-product-index]');
      thumbButtons.forEach((btn, index) => {
        btn.classList.toggle('active', index === this.currentIndex);
        /** @type {HTMLElement} */ (btn).setAttribute('aria-selected', (index === this.currentIndex).toString());
      });
    }
    
    // Mobile thumbnails
    if (this.thumbnailsMobile) {
      const mobileThumbButtons = this.thumbnailsMobile.querySelectorAll('[data-product-index]');
      mobileThumbButtons.forEach((btn, index) => {
        btn.classList.toggle('active', index === this.currentIndex);
        /** @type {HTMLElement} */ (btn).setAttribute('aria-selected', (index === this.currentIndex).toString());
      });
      
      // Auto scroll to active thumbnail on mobile
      // const activeBtn = this.thumbnailsMobile.querySelector('.active');
      // if (activeBtn) {
      //   activeBtn.scrollIntoView({ behavior: 'smooth', inline: 'center' });
      // }
    }
  }

  /**
   * Atualiza avaliação do produto
   */
  updateProductReview() {
    const product = this.productsData[this.currentIndex];
    if (!product || !product.review) return;
    
    const review = product.review;
    
    // Desktop review
    if (this.productReview) {
      this.updateReviewContainer(this.productReview, review, false);
    }
    
    // Mobile review
    if (this.productReviewMobile) {
      this.updateReviewContainer(this.productReviewMobile, review, true);
    }
  }

  /**
   * Atualiza container de avaliação
   * @param {Element} container 
   * @param {Object} review 
   * @param {boolean} isMobile 
   */
  updateReviewContainer(container, review, isMobile = false) {
    const stars = Math.round(/** @type {any} */ (review).estrelas || 0);
    const starSize = isMobile ? 14 : 16;
    const mobileClass = isMobile ? ' mobile' : '';
    
    let starsHtml = '';
    for (let i = 1; i <= 5; i++) {
      if (i <= stars) {
        starsHtml += `<svg class="star filled" width="${starSize}" height="${starSize}" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
      } else {
        starsHtml += `<svg class="star empty" width="${starSize}" height="${starSize}" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
      }
    }
    
    const reviewText = isMobile && /** @type {any} */ (review).texto ? /** @type {any} */ (review).texto.substring(0, 80) + '...' : /** @type {any} */ (review).texto;
    
    container.innerHTML = `
      <div class="star-rating${mobileClass}">
        ${starsHtml}
      </div>
      ${reviewText ? `<p class="review-text${mobileClass}">${reviewText}</p>` : ''}
    `;
  }

  /**
   * Atualiza grade de benefícios
   */
  updateBenefitsGrid() {
    const product = this.productsData[this.currentIndex];
    if (!this.benefitsContainer || !product) return;
    
    const benefits = product.benefits || [];
    
    this.benefitsContainer.innerHTML = '';
    
    if (benefits.length > 0) {
      benefits.forEach(/** @param {any} benefit */ (benefit) => {
        const benefitCard = document.createElement('div');
        benefitCard.className = 'benefit-card';
        
        let iconHtml = '';
        if (benefit.imagem) {
          iconHtml = `<img src="${benefit.imagem}" alt="${benefit.titulo || ''}" class="benefit-large-icon" role="img" aria-label="${benefit.titulo || ''}" loading="lazy" width="64" height="64" />`;
        } else {
          iconHtml = '<div class="benefit-placeholder-icon"></div>';
        }
        
        benefitCard.innerHTML = `
          ${iconHtml}
          ${benefit.titulo ? `<h4 class="benefit-title">${benefit.titulo}</h4>` : ''}
          ${benefit.descricao ? `<p class="benefit-description">${benefit.descricao}</p>` : ''}
        `;
        
        this.benefitsContainer?.appendChild(benefitCard);
      });
    } else {
      // Placeholder benefits
      for (let i = 1; i <= 4; i++) {
        const benefitCard = document.createElement('div');
        benefitCard.className = 'benefit-card placeholder';
        benefitCard.innerHTML = `
          <div class="benefit-placeholder-icon"></div>
          <h4 class="benefit-title">Benefício ${i}</h4>
          <p class="benefit-description">Descrição do benefício será exibida aqui.</p>
        `;
        this.benefitsContainer?.appendChild(benefitCard);
      }
    }
    
    // Reset benefits carousel
    this.benefitsCarouselIndex = 0;
    this.updateMobileBenefitsCarousel();
  }

  /**
   * Atualiza links do CTA
   */
  updateCTALinks() {
    const product = this.productsData[this.currentIndex];
    if (!product) return;
    
    if (this.productCta) {
      /** @type {HTMLAnchorElement} */ (this.productCta).href = product.url || '#';
    }
    
    if (this.productCtaMobile) {
      /** @type {HTMLAnchorElement} */ (this.productCtaMobile).href = product.url || '#';
    }
  }

  /**
   * Preload das próximas imagens para performance
   */
  preloadNextImages() {
    if (!('requestIdleCallback' in window)) return;
    
    requestIdleCallback(() => {
      const nextIndex = (this.currentIndex + 1) % this.productsData.length;
      const prevIndex = this.currentIndex === 0 ? this.productsData.length - 1 : this.currentIndex - 1;
      
      [nextIndex, prevIndex].forEach(index => {
        const product = this.productsData[index];
        if (product && !this.imagePreloadQueue.has(index)) {
          this.imagePreloadQueue.add(index);
          
          if (product.mainImage) {
            const img = new Image();
            img.src = product.mainImage;
          }
          
          if (product.mobileImage && product.mobileImage !== product.mainImage) {
            const img = new Image();
            img.src = product.mobileImage;
          }
        }
      });
    });
  }

  /**
   * Inicializa carrossel de benefícios mobile
   */
  initializeBenefitsCarousel() {
    this.updateMobileBenefitsCarousel();
  }

  /**
   * Manipula navegação anterior dos benefícios
   */
  handleBenefitsPrev() {
    const benefitCards = this.benefitsContainer?.querySelectorAll('.benefit-card');
    if (!benefitCards || benefitCards.length === 0) return;
    
    this.benefitsCarouselIndex = Math.max(0, this.benefitsCarouselIndex - 1);
    this.updateMobileBenefitsCarousel();
  }

  /**
   * Manipula navegação próxima dos benefícios
   */
  handleBenefitsNext() {
    const benefitCards = this.benefitsContainer?.querySelectorAll('.benefit-card');
    if (!benefitCards || benefitCards.length === 0) return;
    
    this.benefitsCarouselIndex = Math.min(benefitCards.length - 1, this.benefitsCarouselIndex + 1);
    this.updateMobileBenefitsCarousel();
  }

  /**
   * Atualiza carrossel de benefícios mobile
   */
  updateMobileBenefitsCarousel() {
    if (window.innerWidth > 768) return;
    
    const benefitCards = this.benefitsContainer?.querySelectorAll('.benefit-card');
    if (!benefitCards || benefitCards.length === 0) return;
    
    // Atualizar botões
    if (this.benefitsPrev) {
      /** @type {HTMLButtonElement} */ (this.benefitsPrev).disabled = this.benefitsCarouselIndex === 0;
    }
    
    if (this.benefitsNext) {
      /** @type {HTMLButtonElement} */ (this.benefitsNext).disabled = this.benefitsCarouselIndex >= benefitCards.length - 1;
    }
    
    // Atualizar indicadores
    if (this.benefitsIndicators) {
      this.benefitsIndicators.innerHTML = '';
      
      for (let i = 0; i < benefitCards.length; i++) {
        const indicator = document.createElement('div');
        indicator.className = `benefits-indicator${i === this.benefitsCarouselIndex ? ' active' : ''}`;
        indicator.addEventListener('click', () => {
          this.benefitsCarouselIndex = i;
          this.updateMobileBenefitsCarousel();
        });
        this.benefitsIndicators.appendChild(indicator);
      }
    }
    
    // Scroll para o card ativo
    // Faz o scroll do card ativo sem alterar a posição do usuário na página
    const activeCard = benefitCards[this.benefitsCarouselIndex];
    if (activeCard && this.benefitsContainer) {
      const container = this.benefitsContainer;
      // Calcula o deslocamento do card ativo em relação ao container
      const cardRect = activeCard.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      // Calcula o scrollLeft necessário para centralizar o card ativo
      const scrollLeft = /** @type {HTMLElement} */ (activeCard).offsetLeft - (container.clientWidth / 2) + (activeCard.clientWidth / 2);
      // Faz o scroll apenas do container, não da página
      container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
    }
  }

  /**
   * Anuncia mudança do produto para screen readers
   * @param {number} index 
   */
  /**
   * Manipula clique em elementos clicáveis
   * @param {Event} event 
   */
  handleClickableElement(event) {
    const product = this.productsData[this.currentIndex];
    if (!product || !product.url) return;
    
    // Navegar para a página do produto
    window.location.href = product.url;
  }

  /**
   * Anuncia mudança do produto para screen readers
   * @param {number} index 
   */
  announceProductChange(index) {
    const product = this.productsData[index];
    if (!product) return;
    
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = `Produto selecionado: ${product.title}`;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      if (announcement.parentNode) {
        document.body.removeChild(announcement);
      }
    }, 200);
  }

  /**
   * Cleanup quando o componente é removido
   */
  disconnectedCallback() {
    if (this.abortController) {
      this.abortController.abort();
    }
    
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    
    this.imagePreloadQueue.clear();
  }
}

// Registrar o custom element
if (!customElements.get('featured-product-stateful')) {
  customElements.define('featured-product-stateful', FeaturedProductStateful);
}

// Adicionar estilos para screen reader only se não existir
if (!document.querySelector('.sr-only-styles')) {
  const style = document.createElement('style');
  style.className = 'sr-only-styles';
  style.textContent = `
    .sr-only {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
  `;
  document.head.appendChild(style);
} 